import React, { useState, useEffect } from 'react';
import {
  Flex,
  Button,
  Modal,
  Box,
  theme,
  Spinner,
  Alert,
} from '@xometry/xometry_loft';
import { css } from '@emotion/react';
import { useMutation } from '@apollo/client';

import {
  RemoveBankAccountMutationResult,
  RemoveBankAccountMutationVariables,
  RemoveBankAccountQuery,
  RemoveStripeCardMutationResult,
  RemoveStripeCardMutationVariables,
  RemoveStripeCardQuery,
} from '../../../schema/generated/schema';
import { getCardImageData } from '../../../utils/helpers';
import { SuccessToast } from '../../../components/SuccessToast';
import { CardPayment, BankAccountPayment } from '../../PaymentMethods';

export interface Props {
  card: CardPayment | null;
  bankAccount: BankAccountPayment | null;
  RemovePaymentMethodModalOpen: boolean;
  methodsRefetch: any;
  setRemovePaymentMethodModalOpen: (openState: boolean) => void;
}

const RemovePaymentMethodModal = (props: Props) => {
  const {
    card,
    bankAccount,
    methodsRefetch,
    RemovePaymentMethodModalOpen,
    setRemovePaymentMethodModalOpen,
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardError, setCardError] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isRemovingPayment, setIsRemovingPayment] = useState(false);

  const [removeStripeCard] = useMutation<
    RemoveStripeCardMutationResult,
    RemoveStripeCardMutationVariables
  >(RemoveStripeCardQuery);

  const [removeBankAccount] = useMutation<
    RemoveBankAccountMutationResult,
    RemoveBankAccountMutationVariables
  >(RemoveBankAccountQuery);

  const modalClose = () => {
    setRemovePaymentMethodModalOpen(false);
    setIsSubmitting(false);
    setCardError(false);
  };

  useEffect(() => {
    let cancelAsyncOps = false;

    const handleRemovePaymentMethod = async () => {
      setIsSubmitting(true);
      if (card) {
        const cardId = card.id;
        const response = await removeStripeCard({
          variables: {
            cardId,
          },
        });
        if (cancelAsyncOps) return;
        setIsSubmitting(false);
        if (response.data?.removeStripeCard.success) {
          methodsRefetch();
          setIsSuccessful(true);
          setCardError(false);
          modalClose();
        } else {
          setCardError(true);
        }
      }
      if (bankAccount) {
        const accountId = bankAccount.id;
        const response = await removeBankAccount({
          variables: {
            accountId,
          },
        });
        if (cancelAsyncOps) return;
        setIsSubmitting(false);
        if (response.data?.removeBankAccount.success) {
          methodsRefetch();
          setIsSuccessful(true);
          setCardError(false);
          modalClose();
        } else {
          setCardError(true);
        }
      }
      setIsRemovingPayment(false);
    };

    if (isRemovingPayment) {
      handleRemovePaymentMethod();
    }

    return () => {
      cancelAsyncOps = true;
    };
  }, [isRemovingPayment]);

  return (
    <>
      <div id="RemovePaymentMethodModal" />
      <Modal
        isOpen={RemovePaymentMethodModalOpen}
        selector="#RemovePaymentMethodModal"
        padding={`${theme.space[5]} ${theme.space[5]} ${theme.space[5]} ${theme.space[6]}`}
        color={theme.colors.grays[0]}
        fontFamily={theme.fonts[0]}
        backgroundColor={theme.colors.white}
        borderRadius={theme.space[1]}
        border="none"
        boxShadow="0 1px 4px 0 rgba(9,44,71,0.15)"
        width="344px"
        display={RemovePaymentMethodModalOpen ? 'initial' : 'none'}
      >
        <Flex justifyContent="space-between">
          <Box
            fontSize={theme.fontSizes[3]}
            fontWeight={theme.fontWeights.regular}
            lineHeight={theme.lineHeights[4]}
            color={theme.colors.darkBlues[90]}
          >
            Remove Payment Method
          </Box>
          <Box
            fontSize={theme.fontSizes[4]}
            lineHeight={theme.lineHeights[3]}
            textAlign="right"
            color={theme.colors.darkBlues[100]}
            onClick={modalClose}
            css={css`
              &:hover {
                cursor: pointer;
              }
            `}
          >
            <i className="fal fa-times" />
          </Box>
        </Flex>
        <Flex
          marginTop={theme.space[1]}
          alignItems="center"
          borderRadius={theme.space[1]}
          padding={`${theme.space[4]} ${theme.space[0]}`}
        >
          <Flex
            width="67px"
            height="44px"
            marginRight={theme.space[4]}
            justifyContent="center"
            alignItems="center"
            borderRadius={theme.space[1]}
          >
            {card ? (
              <img
                src={getCardImageData(card.brand.toLowerCase()).imageUrl}
                alt={getCardImageData(card.brand.toLowerCase()).altText}
                css={css`
                  max-width: 100%;
                `}
              />
            ) : (
              <i
                className="far fa-landmark"
                css={css`
                  color: ${theme.colors.blues[100]};
                  max-width: 100%;
                  font-size: ${theme.fontSizes[6]};
                `}
              />
            )}
          </Flex>
          <Flex flexDirection="column">
            <Flex
              fontSize={theme.fontSizes[2]}
              lineHeight={theme.lineHeights[3]}
            >
              <Box>Ending in</Box>
              <Box
                className="pii-block"
                fontWeight={theme.fontWeights.semibold}
              >
                &nbsp;{card ? card?.last4 : bankAccount?.last4}
              </Box>
            </Flex>
            <Flex
              fontWeight={theme.fontWeights.semibold}
              fontSize={theme.fontSizes[0]}
            >
              <Box className="pii-encrypt" marginRight={theme.space[2]}>
                {card ? card?.name : bankAccount?.bankName?.toUpperCase()}
              </Box>
              {card && (
                <Box className="pii-block">
                  Exp. {card?.expMonth}/{card?.expYear}
                </Box>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex>
          <Box>
            Once a payment method is removed, it will no longer be visible at
            checkout or in your account settings. Existing orders using this
            payment method will not be affected.
          </Box>
        </Flex>
        {cardError && (
          <Alert
            kind="danger"
            marginTop={theme.space[4]}
            fontSize={theme.fontSizes[1]}
            isOpen
          >
            {card
              ? 'There was an error removing your card'
              : 'There was an error removing your account'}
          </Alert>
        )}
        <Flex justifyContent="flex-end" marginTop={theme.space[6]}>
          {!isSubmitting && (
            <>
              <Button
                fontWeight={theme.fontWeights.regular}
                disabled={isSubmitting}
                marginRight={theme.space[2]}
                onClick={modalClose}
              >
                Cancel
              </Button>
              <Button
                kind="danger"
                fontWeight={theme.fontWeights.regular}
                disabled={isSubmitting}
                onClick={() => {
                  setIsRemovingPayment(true);
                }}
              >
                Remove Payment Method
              </Button>
            </>
          )}
          {isSubmitting && <Spinner />}
        </Flex>
      </Modal>
      <SuccessToast
        isSuccessful={isSuccessful}
        setIsSuccessful={setIsSuccessful}
        message="Payment Method successfully removed."
      />
    </>
  );
};

export default RemovePaymentMethodModal;
